import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { X, ArrowRight, ArrowLeft, CheckCircle } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import api from '../utils/api';

interface TourStep {
  route?: string;
  target?: string;
  title: string;
  content: string;
  position?: 'top' | 'bottom' | 'left' | 'right';
  waitForClick?: boolean;
  waitForTargetMs?: number;
  role?: string;
}

interface OnboardingTourProps {
  onComplete: () => void;
}

const OnboardingTour: React.FC<OnboardingTourProps> = ({ onComplete }) => {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  const { user } = useAuth();

  const tourSteps = useMemo(() => {
    const steps: TourStep[] = [
      {
        route: '/app/dashboard',
        title: t('onboardingTour.steps.intro.title'),
        content: t(
          'onboardingTour.steps.intro.content'),
        position: 'bottom'
      },
      {
        route: '/app/settings',
        target: '[data-tour="settings-career-link"]',
        title: t('onboardingTour.steps.career.title'),
        content: t(
          'onboardingTour.steps.career.content'),
        position: 'left'
      },
      {
        route: '/app/settings',
        target: '[data-tour="settings-tab-account"]',
        title: t('onboardingTour.steps.settingsAccountTab.title'),
        content: t(
          'onboardingTour.steps.settingsAccountTab.content'),
        position: 'bottom',
        waitForClick: true
      },
      {
        route: '/app/settings',
        target: '[data-tour="settings-account-fullname"]',
        title: t('onboardingTour.steps.settingsFullName.title'),
        content: t(
          'onboardingTour.steps.settingsFullName.content'),
        position: 'right'
      },
      {
        route: '/app/settings',
        target: '[data-tour="settings-account-language"]',
        title: t('onboardingTour.steps.settingsLanguage.title'),
        content: t(
          'onboardingTour.steps.settingsLanguage.content'),
        position: 'right'
      },
      {
        route: '/app/settings',
        target: '[data-tour="settings-tab-email"]',
        title: t('onboardingTour.steps.settingsEmailTab.title'),
        content: t(
          'onboardingTour.steps.settingsEmailTab.content'),
        position: 'bottom',
        waitForClick: true
      },
      {
        route: '/app/settings',
        target: '[data-tour="settings-email-company-email"]',
        title: t('onboardingTour.steps.settingsEmailCompany.title'),
        content: t(
          'onboardingTour.steps.settingsEmailCompany.content'),
        position: 'right'
      },
      {
        route: '/app/settings',
        target: '[data-tour="settings-email-app-password"]',
        title: t('onboardingTour.steps.settingsEmailPassword.title'),
        content: t(
          'onboardingTour.steps.settingsEmailPassword.content'),
        position: 'right'
      },
      {
        route: '/app/settings',
        target: '[data-tour="settings-tab-interview"]',
        title: t('onboardingTour.steps.settingsInterviewTab.title'),
        content: t(
          'onboardingTour.steps.settingsInterviewTab.content'),
        position: 'bottom',
        waitForClick: true
      },
      {
        route: '/app/settings',
        target: '[data-tour="settings-interview-auto-invite"]',
        title: t('onboardingTour.steps.settingsAutoInvite.title'),
        content: t(
          'onboardingTour.steps.settingsAutoInvite.content'),
        position: 'right'
      },
      {
        route: '/app/interview-flows',
        target: '[data-tour="flows-create"]',
        title: t('onboardingTour.steps.flows.title'),
        content: t(
          'onboardingTour.steps.flows.content'),
        position: 'left',
        waitForClick: true
      },
      {
        route: '/app/interview-flows/new',
        target: '[data-tour="flows-builder-name"]',
        title: t('onboardingTour.steps.flowBuilderName.title'),
        content: t(
          'onboardingTour.steps.flowBuilderName.content'),
        position: 'right',
        waitForTargetMs: 8000
      },
      {
        route: '/app/interview-flows/new',
        target: '[data-tour="flows-builder-add-block"]',
        title: t('onboardingTour.steps.flowBuilderAddBlock.title'),
        content: t(
          'onboardingTour.steps.flowBuilderAddBlock.content'),
        position: 'left',
        waitForClick: true
      },
      {
        route: '/app/interview-flows/new',
        target: '[data-tour="flows-builder-block-picker"]',
        title: t('onboardingTour.steps.flowBuilderBlockPicker.title'),
        content: t(
          'onboardingTour.steps.flowBuilderBlockPicker.content'),
        position: 'bottom',
        waitForTargetMs: 8000
      },
      {
        route: '/app/positions',
        target: '[data-tour="positions-create"]',
        title: t('onboardingTour.steps.positions.title'),
        content: t(
          'onboardingTour.steps.positions.content'),
        position: 'left',
        waitForClick: true
      },
      {
        route: '/app/positions',
        target: '[data-tour="positions-modal"]',
        title: t('onboardingTour.steps.positionModal.title'),
        content: t(
          'onboardingTour.steps.positionModal.content'),
        position: 'bottom',
        waitForTargetMs: 8000
      },
      {
        route: '/app/positions',
        target: '[data-tour="positions-modal-job-title"]',
        title: t('onboardingTour.steps.positionModalTitle.title'),
        content: t(
          'onboardingTour.steps.positionModalTitle.content'),
        position: 'right'
      },
      {
        route: '/app/positions',
        target: '[data-tour="positions-modal-default-hr-flow"]',
        title: t('onboardingTour.steps.positionModalHrFlow.title'),
        content: t(
          'onboardingTour.steps.positionModalHrFlow.content'),
        position: 'right'
      },
      {
        route: '/app/positions',
        target: '[data-tour="positions-modal-default-technical-flow"]',
        title: t('onboardingTour.steps.positionModalTechFlow.title'),
        content: t(
          'onboardingTour.steps.positionModalTechFlow.content'),
        position: 'right'
      },
      {
        route: '/app/applications',
        target: '[data-tour="applications-cv-upload"]',
        title: t('onboardingTour.steps.applications.title'),
        content: t(
          'onboardingTour.steps.applications.content'),
        position: 'left',
        waitForClick: true
      },
      {
        route: '/app/applications',
        target: '[data-tour="applications-upload-modal"]',
        title: t('onboardingTour.steps.applicationsUploadModal.title'),
        content: t(
          'onboardingTour.steps.applicationsUploadModal.content'),
        position: 'bottom',
        waitForTargetMs: 8000
      },
      {
        route: '/app/interviews',
        target: '[data-tour="interviews-create"]',
        title: t('onboardingTour.steps.interviews.title'),
        content: t(
          'onboardingTour.steps.interviews.content'),
        position: 'left',
        waitForClick: true
      },
      {
        route: '/app/interviews',
        target: '[data-tour="interviews-invite-modal"]',
        title: t('onboardingTour.steps.interviewsInviteModal.title'),
        content: t(
          'onboardingTour.steps.interviewsInviteModal.content'),
        position: 'bottom',
        waitForTargetMs: 8000
      },
      {
        route: '/app/team',
        target: '[data-tour="team-title"]',
        title: t('onboardingTour.steps.team.title'),
        content: t(
          'onboardingTour.steps.team.content'),
        position: 'left'
      },
      {
        route: '/app/dashboard',
        title: t('onboardingTour.steps.done.title'),
        content: t(
          'onboardingTour.steps.done.content'),
        position: 'bottom'
      }
    ];

    return steps.filter((step) => {
      if (!step.role) return true;
      return user?.role === step.role;
    });
  }, [i18n.language, t, user?.role]);

  const [currentStep, setCurrentStep] = useState(0);
  const [isVisible, setIsVisible] = useState(true);
  const [highlightPosition, setHighlightPosition] = useState({ top: 0, left: 0, width: 0, height: 0 });
  const pendingAdvanceRef = useRef(false);
  const cardRef = useRef<HTMLDivElement | null>(null);
  const [cardStyle, setCardStyle] = useState<{ top: number; left: number } | null>(null);
  const [arrowStyle, setArrowStyle] = useState<React.CSSProperties | null>(null);

  const handleComplete = useCallback(async () => {
    try {
      await api.post('/api/onboarding-tour/complete', {});
      setIsVisible(false);
      onComplete();
    } catch (error) {
      console.error('Error completing onboarding:', error);
    }
  }, [onComplete]);

  const advanceToNext = useCallback(() => {
    if (pendingAdvanceRef.current) return;
    pendingAdvanceRef.current = true;

    setCurrentStep((prev) => {
      const lastIndex = Math.max(0, tourSteps.length - 1);
      if (prev >= lastIndex) {
        window.setTimeout(() => {
          handleComplete();
        }, 0);
        return prev;
      }
      return prev + 1;
    });

    window.setTimeout(() => {
      pendingAdvanceRef.current = false;
    }, 300);
  }, [handleComplete, tourSteps.length]);

  const updateHighlight = useCallback((element: Element | null) => {
    if (!element) {
      setHighlightPosition({ top: 0, left: 0, width: 0, height: 0 });
      return;
    }

    const rect = element.getBoundingClientRect();
    setHighlightPosition({
      top: rect.top,
      left: rect.left,
      width: rect.width,
      height: rect.height,
    });

    (element as HTMLElement).scrollIntoView({ behavior: 'smooth', block: 'center' });
  }, []);

  const waitForElement = useCallback((selector: string, timeoutMs = 4000) => {
    return new Promise<Element | null>((resolve) => {
      const started = Date.now();
      const tick = () => {
        const el = document.querySelector(selector);
        if (el) {
          resolve(el);
          return;
        }
        if (Date.now() - started >= timeoutMs) {
          resolve(null);
          return;
        }
        window.setTimeout(tick, 120);
      };
      tick();
    });
  }, []);

  useEffect(() => {
    const step = tourSteps[currentStep];
    if (!step) return;

    let cancelled = false;
    let targetEl: Element | null = null;

    const run = async () => {
      if (step.route && location.pathname !== step.route) {
        updateHighlight(null);
        navigate(step.route);
        return;
      }

      if (!step.target) {
        updateHighlight(null);
        return;
      }

      targetEl = await waitForElement(step.target, step.waitForTargetMs ?? 4000);
      if (cancelled) return;

      if (!targetEl) {
        updateHighlight(null);
        return;
      }

      updateHighlight(targetEl);
    };

    run();

    const onResize = () => {
      if (step.target) {
        const el = document.querySelector(step.target);
        updateHighlight(el);
      }
    };
    window.addEventListener('resize', onResize);
    window.addEventListener('scroll', onResize, true);

    return () => {
      cancelled = true;
      window.removeEventListener('resize', onResize);
      window.removeEventListener('scroll', onResize, true);
    };
  }, [advanceToNext, currentStep, location.pathname, navigate, tourSteps, updateHighlight, waitForElement]);

  useEffect(() => {
    const step = tourSteps[currentStep];
    if (!step) return;

    const updateCard = () => {
      const rect = {
        top: highlightPosition.top,
        left: highlightPosition.left,
        width: highlightPosition.width,
        height: highlightPosition.height
      };

      const cardRect = cardRef.current?.getBoundingClientRect();
      if (!step.target || !cardRect || rect.width <= 0 || rect.height <= 0) {
        const margin = 14;
        const estCardWidth = 384;
        const estCardHeight = 420;
        const top = Math.max(margin, window.innerHeight - 32 - estCardHeight);
        const left = Math.max(margin, window.innerWidth - 32 - estCardWidth);
        setCardStyle({ top, left });
        setArrowStyle(null);
        return;
      }

      const margin = 14;
      const gap = 14;

      const topSpace = rect.top;
      const bottomSpace = window.innerHeight - (rect.top + rect.height);
      const leftSpace = rect.left;
      const rightSpace = window.innerWidth - (rect.left + rect.width);

      const preferred = step.position;
      const fits = (placement: 'top' | 'bottom' | 'left' | 'right') => {
        if (placement === 'top') return topSpace >= cardRect.height + gap + margin;
        if (placement === 'bottom') return bottomSpace >= cardRect.height + gap + margin;
        if (placement === 'left') return leftSpace >= cardRect.width + gap + margin;
        return rightSpace >= cardRect.width + gap + margin;
      };

      const candidates: Array<'top' | 'bottom' | 'left' | 'right'> = preferred
        ? [preferred, 'bottom', 'top', 'right', 'left']
        : ['bottom', 'top', 'right', 'left'];

      const placement = candidates.find(fits) || 'bottom';

      const clamp = (value: number, min: number, max: number) => Math.max(min, Math.min(max, value));

      const targetCenterX = rect.left + rect.width / 2;
      const targetCenterY = rect.top + rect.height / 2;

      let top = 0;
      let left = 0;

      if (placement === 'top') {
        top = rect.top - gap - cardRect.height;
        left = targetCenterX - cardRect.width / 2;
      } else if (placement === 'bottom') {
        top = rect.top + rect.height + gap;
        left = targetCenterX - cardRect.width / 2;
      } else if (placement === 'left') {
        top = targetCenterY - cardRect.height / 2;
        left = rect.left - gap - cardRect.width;
      } else {
        top = targetCenterY - cardRect.height / 2;
        left = rect.left + rect.width + gap;
      }

      top = clamp(top, margin, window.innerHeight - margin - cardRect.height);
      left = clamp(left, margin, window.innerWidth - margin - cardRect.width);
      setCardStyle({ top, left });

      const arrowSize = 10;
      const arrowInset = 18;

      if (placement === 'top') {
        const arrowLeft = clamp(targetCenterX - left, arrowInset, cardRect.width - arrowInset);
        setArrowStyle({
          position: 'absolute',
          top: '100%',
          left: arrowLeft,
          transform: 'translateX(-50%)',
          width: 0,
          height: 0,
          borderLeft: `${arrowSize}px solid transparent`,
          borderRight: `${arrowSize}px solid transparent`,
          borderTop: `${arrowSize}px solid white`
        });
      } else if (placement === 'bottom') {
        const arrowLeft = clamp(targetCenterX - left, arrowInset, cardRect.width - arrowInset);
        setArrowStyle({
          position: 'absolute',
          bottom: '100%',
          left: arrowLeft,
          transform: 'translateX(-50%)',
          width: 0,
          height: 0,
          borderLeft: `${arrowSize}px solid transparent`,
          borderRight: `${arrowSize}px solid transparent`,
          borderBottom: `${arrowSize}px solid white`
        });
      } else if (placement === 'left') {
        const arrowTop = clamp(targetCenterY - top, arrowInset, cardRect.height - arrowInset);
        setArrowStyle({
          position: 'absolute',
          left: '100%',
          top: arrowTop,
          transform: 'translateY(-50%)',
          width: 0,
          height: 0,
          borderTop: `${arrowSize}px solid transparent`,
          borderBottom: `${arrowSize}px solid transparent`,
          borderLeft: `${arrowSize}px solid white`
        });
      } else {
        const arrowTop = clamp(targetCenterY - top, arrowInset, cardRect.height - arrowInset);
        setArrowStyle({
          position: 'absolute',
          right: '100%',
          top: arrowTop,
          transform: 'translateY(-50%)',
          width: 0,
          height: 0,
          borderTop: `${arrowSize}px solid transparent`,
          borderBottom: `${arrowSize}px solid transparent`,
          borderRight: `${arrowSize}px solid white`
        });
      }
    };

    const raf = window.requestAnimationFrame(updateCard);
    return () => window.cancelAnimationFrame(raf);
  }, [currentStep, highlightPosition.height, highlightPosition.left, highlightPosition.top, highlightPosition.width, tourSteps]);

  const handleNext = () => {
    if (currentStep < tourSteps.length - 1) {
      advanceToNext();
    } else {
      handleComplete();
    }
  };

  const handlePrevious = () => {
    if (currentStep > 0) {
      setCurrentStep((prev) => Math.max(0, prev - 1));
    }
  };

  const handleSkip = () => {
    if (window.confirm(t('onboardingTour.confirmSkip'))) {
      handleComplete();
    }
  };

  if (!isVisible) return null;

  const step = tourSteps[currentStep];
  const progress = ((currentStep + 1) / tourSteps.length) * 100;

  if (!step) return null;

  return (
    <>
      {/* Overlay */}
      <div className="fixed inset-0 bg-black bg-opacity-50 z-40" style={{ pointerEvents: 'none' }}>
        {/* Highlight */}
        {step.target && highlightPosition.width > 0 && highlightPosition.height > 0 && (
          <div
            className="absolute border-4 border-lotus-gold rounded-lg transition-all duration-300"
            style={{
              top: `${highlightPosition.top - 8}px`,
              left: `${highlightPosition.left - 8}px`,
              width: `${highlightPosition.width + 16}px`,
              height: `${highlightPosition.height + 16}px`,
              boxShadow: '0 0 0 9999px rgba(0, 0, 0, 0.5)',
              pointerEvents: 'none'
            }}
          />
        )}
      </div>

      {/* Tour Card */}
      <div
        ref={cardRef}
        className="fixed z-50 w-96 max-w-[calc(100vw-1rem)] bg-white rounded-lg shadow-2xl border-2 border-lotus-gold"
        style={cardStyle ? { top: `${cardStyle.top}px`, left: `${cardStyle.left}px` } : undefined}
      >
        {arrowStyle && <div style={arrowStyle} />}
        {/* Progress Bar */}
        <div className="h-2 bg-gray-200 rounded-t-lg overflow-hidden">
          <div
            className="h-full bg-lotus-gold transition-all duration-300"
            style={{ width: `${progress}%` }}
          />
        </div>

        {/* Content */}
        <div className="p-6">
          <div className="flex items-start justify-between mb-4">
            <div className="flex-1">
              <h3 className="text-xl font-bold text-gray-900 mb-2">{step.title}</h3>
              <p className="text-gray-600">{step.content}</p>
            </div>
            <button
              onClick={handleSkip}
              className="text-gray-400 hover:text-gray-600 ml-4"
            >
              <X className="w-5 h-5" />
            </button>
          </div>

          {/* Step Indicator */}
          <div className="flex items-center gap-2 mb-4">
            {tourSteps.map((_, index) => (
              <div
                key={index}
                className={`h-2 flex-1 rounded-full ${
                  index === currentStep
                    ? 'bg-lotus-gold'
                    : index < currentStep
                    ? 'bg-green-500'
                    : 'bg-gray-200'
                }`}
              />
            ))}
          </div>

          {/* Actions */}
          <div className="flex items-center justify-between">
            <button
              onClick={handlePrevious}
              disabled={currentStep === 0}
              className={`flex items-center gap-2 px-4 py-2 rounded ${
                currentStep === 0
                  ? 'text-gray-400 cursor-not-allowed'
                  : 'text-gray-700 hover:bg-gray-100'
              }`}
            >
              <ArrowLeft className="w-4 h-4" />
              {t('common.back')}
            </button>

            <span className="text-sm text-gray-500">
              {currentStep + 1} / {tourSteps.length}
            </span>

            <button
              onClick={handleNext}
              className="flex items-center gap-2 btn-primary"
            >
              {currentStep === tourSteps.length - 1 ? (
                <>
                  <CheckCircle className="w-4 h-4" />
                  {t('onboardingTour.complete')}
                </>
              ) : (
                <>
                  {t('common.next')}
                  <ArrowRight className="w-4 h-4" />
                </>
              )}
            </button>
          </div>

          {/* Skip Button */}
          <button
            onClick={handleSkip}
            className="w-full mt-4 text-sm text-gray-500 hover:text-gray-700"
          >
            {t('onboardingTour.skip')}
          </button>
        </div>
      </div>
    </>
  );
};

export default OnboardingTour;
